import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  steps: string[];
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  currentStep,
  totalSteps,
  steps,
}) => {
  const { t } = useTranslation();
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="w-full mb-10">
      {/* Progress track with refined styling */}
      <div className="relative h-1.5 bg-tiber-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-y-0 left-0 bg-linear-to-r from-tiber-500 via-tiber-600 to-tiber-700 rounded-full shadow-sm"
        />
      </div>

      {/* Step indicators - compact on mobile, full on desktop */}
      <div className="flex justify-between gap-1 mt-6">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < currentStep;
          const isCurrent = stepNumber === currentStep;

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              className="flex flex-col items-center flex-1 min-w-0"
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: index * 0.05 + 0.1, type: "spring", stiffness: 400, damping: 25 }}
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold mb-2 shrink-0 transition-colors duration-200 ${
                  isCompleted
                    ? "bg-tiber-600 text-white shadow-md shadow-tiber-600/25"
                    : isCurrent
                      ? "bg-tiber-500 text-white ring-2 ring-tiber-600 ring-offset-2"
                      : "bg-tiber-100 text-tiber-700"
                }`}
              >
                {isCompleted ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  stepNumber
                )}
              </motion.div>
              <span
                className={`text-[11px] sm:text-xs text-center max-w-[70px] sm:max-w-[90px] leading-tight font-medium transition-colors duration-200 ${
                  isCurrent ? "text-tiber-800" : "text-tiber-700/70"
                }`}
              >
                {step}
              </span>
            </motion.div>
          );
        })}
      </div>

      <p className="mt-3 text-center text-sm text-tiber-600/80 font-medium">
        {t("onboarding.stepProgress", {
          current: currentStep,
          total: totalSteps,
        })}
      </p>
    </div>
  );
};
